import React, { useState, useMemo, useEffect } from "react";
import api from "../../../services/api";
import { useTheme } from "styled-components";
import {
  FiArrowLeft,
  FiUser,
  FiMapPin,
  FiPhone,
  FiMail,
  FiCalendar,
  FiDollarSign,
  FiTrendingUp,
  FiPackage,
  FiPercent,
  FiShoppingCart,
  FiClock,
  FiTarget,
  FiActivity,
  FiFileText,
  FiLayers,
  FiBox,
  FiTruck,
  FiCpu,
  FiTool,
  FiDownload,
  FiHash,
  FiTag,
} from "react-icons/fi";
import { GoOrganization, GoLocation, GoGraph, GoLaw } from "react-icons/go";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Container } from "./styles";
import ClientDetailLoadingSkeleton from "./ClientDetailLoadingSkeleton";

// ==================== CRONOGRAMA REFORMA TRIBUTÁRIA ====================
// Durante a transição, os impostos antigos diminuem e os novos aumentam
const CRONOGRAMA_REFORMA = {
  2026: { cbs: 0.009, ibs: 0.001, reducaoAntigos: 0.00, label: "Início" },
  2027: { cbs: 0.088, ibs: 0.001, reducaoAntigos: 0.00, label: "Início" },
  2028: { cbs: 0.088, ibs: 0.001, reducaoAntigos: 0.00, label: "Trans." },
  2029: { cbs: 0.088, ibs: 0.023, reducaoAntigos: 0.10, label: "40%" },
  2030: { cbs: 0.088, ibs: 0.046, reducaoAntigos: 0.20, label: "60%" },
  2031: { cbs: 0.088, ibs: 0.069, reducaoAntigos: 0.30, label: "80%" },
  2032: { cbs: 0.088, ibs: 0.092, reducaoAntigos: 0.40, label: "Pleno" },
  2033: { cbs: 0.088, ibs: 0.177, reducaoAntigos: 1.00, label: "100%" },
};

// Função para formatar valores monetários
const formatNumber = (value) => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatDate = (date) => {
  if (!date) return "Não informado";
  return new Intl.DateTimeFormat("pt-BR").format(new Date(date));
};

const formatCNPJ = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length === 14) {
    return cleaned.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }
  return cnpj;
};

export default function ClientDetail({ cliente, onVoltar, initialTab = "geral" }) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedCenario, setSelectedCenario] = useState("2027");

  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
     const fetchDetails = async () => {
        if (!cliente?.id) return;
        const projectId = localStorage.getItem("vidal_selected_project");
        if (!projectId) return;

        try {
           setLoading(true);
           const res = await api.get(`/clients/${projectId}/detail/${cliente.id}`);
           if (res.data.success) {
               setDetailData(res.data.data);
           }
        } catch(e) {
           console.error("Erro detalhes cliente", e);
        } finally {
           setLoading(false);
        }
     }
     fetchDetails();
  }, [cliente]);

  // Dados reais da API ou fallback para cliente prop
  const clienteData = useMemo(() => {
      if (detailData?.cliente) {
          return { 
              ...detailData.cliente,
              razaoSocial: detailData.cliente.razaoSocial || detailData.cliente.nome 
          };
      }
      return cliente || {};
  }, [detailData, cliente]);

  const evolucaoVendas = useMemo(() => detailData?.evolucao || [], [detailData]);
  const distribuicaoImpostos = useMemo(() => detailData?.impostos || [], [detailData]);
  const produtosComprados = useMemo(() => detailData?.produtos || [], [detailData]);
  const historicoCompras = useMemo(() => detailData?.historico || [], [detailData]);


  // Cálculos de métricas
  const totalImpostos = distribuicaoImpostos.reduce(
    (sum, item) => sum + item.value,
    0
  );
  const cargaTributaria = (
    (totalImpostos / clienteData.faturamento) *
    100
  ).toFixed(1);
  const tempoRelacionamento = useMemo(() => {
    if (!clienteData.primeiraCompra) return 0;
    const inicio = new Date(clienteData.primeiraCompra);
    const hoje = new Date();
    const meses =
      (hoje.getFullYear() - inicio.getFullYear()) * 12 +
      (hoje.getMonth() - inicio.getMonth());
    return meses;
  }, [clienteData.primeiraCompra]);

  // Cálculos da Reforma Tributária com transição gradual
  const reformaKpis = useMemo(() => {
    const cenarioAno = parseInt(selectedCenario);
    const cenario = CRONOGRAMA_REFORMA[cenarioAno] || CRONOGRAMA_REFORMA["2027"];
    const aliquotaCBS = cenario.cbs;
    const aliquotaIBS = cenario.ibs;
    const reducaoAntigos = cenario.reducaoAntigos || 0;

    // Valores base dos impostos atuais (2024)
    const icmsBase = clienteData.icms || 0;
    const pisBase = (clienteData.pisCofins || 0) * 0.165 / 0.925;
    const cofinsBase = (clienteData.pisCofins || 0) * 0.76 / 0.925;
    const ipiBase = clienteData.is || 0;

    // Aplicar redução gradual dos impostos antigos
    const icms = icmsBase * (1 - reducaoAntigos);
    const pis = pisBase * (1 - reducaoAntigos);
    const cofins = cofinsBase * (1 - reducaoAntigos);
    const ipi = ipiBase * (1 - reducaoAntigos);
    const debitosAtuais = icms + pis + cofins + ipi;

    // Cálculo IVA Dual com alíquotas do cenário selecionado
    const faturamento = clienteData.faturamento || 0;
    const cbsProjetado = faturamento * aliquotaCBS;
    const ibsProjetado = faturamento * aliquotaIBS;
    const totalIvaDual = cbsProjetado + ibsProjetado;

    // Impacto
    const impacto = totalIvaDual - debitosAtuais;
    const variacaoPercentual = debitosAtuais > 0 ? ((impacto / debitosAtuais) * 100) : 0;

    return {
      icms,
      pis,
      cofins,
      ipi,
      debitosAtuais,
      cbs: cbsProjetado,
      ibs: ibsProjetado,
      ivaDual: totalIvaDual,
      aliquotaCBS: aliquotaCBS * 100,
      aliquotaIBS: aliquotaIBS * 100,
      impacto,
      variacaoPercentual,
      reducaoAplicada: reducaoAntigos * 100
    };
  }, [clienteData, selectedCenario]);

  if (loading) {
    return (
      <Container theme={theme}>
        <ClientDetailLoadingSkeleton 
          isDark={theme.body !== "#fafbfc"} 
          onVoltar={onVoltar}
        />
      </Container>
    );
  }

  return (
    <Container theme={theme}>
      <div className="content-wrapper">
        {/* Botão Voltar */}
        <button className="back-button" onClick={onVoltar}>
          <FiArrowLeft />
          <span>Voltar</span>
        </button>

        {/* Header padronizado */}
        <div className="header-info">
          <div className="header-details">
            <h3>{clienteData.nome}</h3>
            <div className="header-meta">
              <span className="status-tag">Ativo</span>
              <span className="update-tag">{formatCNPJ(clienteData.cnpj)} · {clienteData.segmento} · {clienteData.cidade} - {clienteData.uf} · {tempoRelacionamento} meses</span>
            </div>
          </div>
        </div>

      {/* Tabs de Navegação */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === "geral" ? "active" : ""}`}
          onClick={() => setActiveTab("geral")}
        >
          <FiLayers />
          <span>Geral</span>
        </button>
        <button
          className={`tab ${activeTab === "reforma" ? "active" : ""}`}
          onClick={() => setActiveTab("reforma")}
        >
          <GoLaw />
          <span>Reforma Tributária</span>
        </button>
      </div>

      {/* Conteúdo da Aba Geral */}
      {activeTab === "geral" && (
        <>
          {/* KPIs Comercial */}
          <div className="kpi-grid kpi-grid-4">
            <div className="kpi-card">
              <div className="card-content">
                <div className="thumb"><FiDollarSign /></div>
                <div className="text">
                  <p>{formatNumber(clienteData.faturamento)}</p>
                  <p>Faturamento Total</p>
                </div>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="card-content">
                <div className="thumb"><FiShoppingCart /></div>
                <div className="text">
                  <p>{clienteData.totalPedidos}</p>
                  <p>Total de Pedidos</p>
                </div>
              </div>
            </div>
            <div className="kpi-card warning">
              <div className="card-content">
                <div className="thumb"><FiTarget /></div>
                <div className="text">
                  <p>{formatNumber(clienteData.ticketMedio)}</p>
                  <p>Ticket Médio</p>
                </div>
              </div>
            </div>
            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb"><FiPackage /></div>
                <div className="text">
                  <p>{clienteData.produtosUnicos}</p>
                  <p>Produtos Únicos</p>
                </div>
              </div>
            </div>
          </div>

          {/* KPIs Tributário */}
          <div className="kpi-grid kpi-grid-3">
            <div className="kpi-card warning">
              <div className="card-content">
                <div className="thumb"><FiFileText /></div>
                <div className="text">
                  <p>{formatNumber(clienteData.icms)}</p>
                  <p>ICMS · {((clienteData.icms / clienteData.faturamento) * 100).toFixed(1)}%</p>
                </div>
              </div>
            </div>
            <div className="kpi-card danger">
              <div className="card-content">
                <div className="thumb"><FiFileText /></div>
                <div className="text">
                  <p>{formatNumber(clienteData.pisCofins * 0.165 / 0.925)}</p>
                  <p>PIS · 1.65%</p>
                </div>
              </div>
            </div>
            <div className="kpi-card danger">
              <div className="card-content">
                <div className="thumb"><FiFileText /></div>
                <div className="text">
                  <p>{formatNumber(clienteData.pisCofins * 0.76 / 0.925)}</p>
                  <p>COFINS · 7.6%</p>
                </div>
              </div>
            </div>
            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb"><FiFileText /></div>
                <div className="text">
                  <p>{formatNumber(clienteData.is || 0)}</p>
                  <p>IPI · {((clienteData.is || 0) / clienteData.faturamento * 100).toFixed(2)}%</p>
                </div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="card-content">
                <div className="thumb"><FiDollarSign /></div>
                <div className="text">
                  <p>{formatNumber(totalImpostos)}</p>
                  <p>Débitos Totais</p>
                </div>
              </div>
            </div>
            <div className="kpi-card primary">
              <div className="card-content">
                <div className="thumb"><FiPercent /></div>
                <div className="text">
                  <p>{cargaTributaria}%</p>
                  <p>Alíquota Efetiva</p>
                </div>
              </div>
            </div>
          </div>

          {/* KPIs Performance */}
          <div className="kpi-grid kpi-grid-3">
            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb"><FiCalendar /></div>
                <div className="text">
                  <p>{formatDate(clienteData.primeiraCompra)}</p>
                  <p>Primeira Compra</p>
                </div>
              </div>
            </div>
            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb"><FiClock /></div>
                <div className="text">
                  <p>{formatDate(clienteData.ultimaCompra)}</p>
                  <p>Última Compra</p>
                </div>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="card-content">
                <div className="thumb"><FiActivity /></div>
                <div className="text">
                  <p>{tempoRelacionamento} <span style={{fontSize:'0.6em',opacity:0.7}}>meses</span></p>
                  <p>Relacionamento</p>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficos lado a lado */}
          <div className="charts-row">
            {/* Evolução de Vendas */}
            <div className="section-card chart-half">
              <div className="card-header">
                <div className="header-title"><FiTrendingUp /> Evolução de Vendas</div>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={evolucaoVendas} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.border} vertical={false} />
                    <XAxis dataKey="mes" tick={{ fill: theme.text, fontSize: 10, opacity: 0.7 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: theme.text, fontSize: 9, opacity: 0.6 }} axisLine={false} tickLine={false} width={60} tickFormatter={(value) => formatNumber(value)} />
                    <Tooltip cursor={false} contentStyle={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: "8px" }} formatter={(value) => [formatCurrency(value), ""]} />
                    <Bar dataKey="vendas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="impostos" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Composição Tributária */}
            <div className="section-card chart-half">
              <div className="card-header">
                <div className="header-title"><GoGraph /> Composição Tributária</div>
              </div>
              <div className="donut-wrapper">
                <div className="donut-chart">
                  {(() => {
                    const activeTaxes = distribuicaoImpostos.filter((t) => t.value > 0);
                    const total = distribuicaoImpostos.reduce((sum, t) => sum + t.value, 0);
                    let currentAngle = -90;
                    const radius = 80, innerRadius = 50, cx = 100, cy = 100;
                    return (
                      <>
                        <svg width="180" height="180" viewBox="0 0 200 200">
                          {activeTaxes.map((tax, index) => {
                            const percentage = tax.value / total;
                            const angle = percentage * 360;
                            const startAngle = currentAngle;
                            const startRad = (startAngle * Math.PI) / 180;
                            const endRad = ((startAngle + angle) * Math.PI) / 180;
                            const largeArc = angle > 180 ? 1 : 0;
                            const x1 = cx + radius * Math.cos(startRad);
                            const y1 = cy + radius * Math.sin(startRad);
                            const x2 = cx + radius * Math.cos(endRad);
                            const y2 = cy + radius * Math.sin(endRad);
                            const ix1 = cx + innerRadius * Math.cos(startRad);
                            const iy1 = cy + innerRadius * Math.sin(startRad);
                            const ix2 = cx + innerRadius * Math.cos(endRad);
                            const iy2 = cy + innerRadius * Math.sin(endRad);
                            const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;
                            const midAngle = startAngle + angle / 2;
                            const midRad = (midAngle * Math.PI) / 180;
                            const labelR = (radius + innerRadius) / 2;
                            const labelX = cx + labelR * Math.cos(midRad);
                            const labelY = cy + labelR * Math.sin(midRad);
                            currentAngle += angle;
                            return (
                              <g key={index}>
                                <path d={path} fill={tax.color} />
                                {percentage > 0.08 && (
                                  <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="12" fontWeight="600">
                                    {(percentage * 100).toFixed(0)}%
                                  </text>
                                )}
                              </g>
                            );
                          })}
                        </svg>
                        <div className="donut-center">
                          <span className="donut-total">{formatNumber(total)}</span>
                          <span className="donut-label">Total</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="categories-list">
                  {distribuicaoImpostos.map((item, index) => {
                    const total = distribuicaoImpostos.reduce((sum, t) => sum + t.value, 0);
                    const percent = ((item.value / total) * 100).toFixed(1);
                    return (
                      <div key={index} className="category-item">
                        <div className="category-color" style={{ backgroundColor: item.color }}></div>
                        <div className="category-info">
                          <span className="category-name">{item.name}</span>
                          <span className="category-percent">{percent}%</span>
                        </div>
                        <span className="category-value">{formatNumber(item.value)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Produtos Comprados */}
          <div className="stats-card">
            <div className="card-header">
              <div className="header-title">
                <FiPackage />
                <h4>Produtos Comprados</h4>
              </div>
              <div className="header-badge">
                {produtosComprados.length} produtos
              </div>
            </div>
            <div className="products-table">
              <div className="table-header">
                <div className="col-produto">Produto</div>
                <div className="col-quantidade">Quantidade</div>
                <div className="col-icms">ICMS</div>
                <div className="col-valor">Valor Total</div>
                <div className="col-participacao">Participação</div>
              </div>
              {produtosComprados.length === 0 && (
                <div className="table-row" style={{ justifyContent: 'center', padding: 20, color: theme.textSecondary }}>
                  Nenhum produto encontrado para este período.
                </div>
              )}
              {produtosComprados.map((produto, index) => {
                const maxValor = produtosComprados[0]?.valorTotal || 1;
                const tagColor = produto.cor || ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'][index % 5];
                return (
                  <div key={produto.id} className="table-row">
                    <div className="col-produto">
                      <div className="product-info">
                        <div
                          className="product-color"
                          style={{ backgroundColor: tagColor }}
                        ></div>
                        <div className="product-details">
                          <span className="product-name">{produto.nome}</span>
                          <span className="product-code">{produto.ncm}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-quantidade">
                      <span className="quantity-badge">
                        {produto.quantidade.toLocaleString("pt-BR")} {produto.unidade}
                      </span>
                    </div>
                    <div className="col-icms">
                      <span className="value-text">{formatNumber(produto.valorIcms)}</span>
                    </div>
                    <div className="col-valor">
                      <span className="value-text">{formatCurrency(produto.valorTotal)}</span>
                    </div>
                    <div className="col-participacao">
                      <div className="participation-bar">
                        <div
                          className="participation-fill"
                          style={{
                            width: `${(produto.valorTotal / maxValor) * 100}%`,
                            backgroundColor: tagColor,
                          }}
                        ></div>
                        <span className="participation-text">
                          {(produto.participacao || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Conteúdo da Aba Reforma */}
      {activeTab === "reforma" && (
        <>
          {/* Timeline da Reforma Tributária */}
          <div className="reform-timeline">
            <button 
              className={`timeline-item ${selectedCenario === "2027" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2027")}
            >
              <span className="timeline-year">2027</span>
              <span className="timeline-label">Início IVA Dual</span>
            </button>
            <button 
              className={`timeline-item ${selectedCenario === "2028" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2028")}
            >
              <span className="timeline-year">2028</span>
              <span className="timeline-label">Transição 0%</span>
            </button>
            <button 
              className={`timeline-item ${selectedCenario === "2029" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2029")}
            >
              <span className="timeline-year">2029</span>
              <span className="timeline-label">Transição 10%</span>
            </button>
            <button 
              className={`timeline-item ${selectedCenario === "2030" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2030")}
            >
              <span className="timeline-year">2030</span>
              <span className="timeline-label">Transição 20%</span>
            </button>
            <button 
              className={`timeline-item ${selectedCenario === "2031" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2031")}
            >
              <span className="timeline-year">2031</span>
              <span className="timeline-label">Transição 30%</span>
            </button>
            <button 
              className={`timeline-item ${selectedCenario === "2032" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2032")}
            >
              <span className="timeline-year">2032</span>
              <span className="timeline-label">Transição 40%</span>
            </button>
            <button 
              className={`timeline-item ${selectedCenario === "2033" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2033")}
            >
              <span className="timeline-year">2033</span>
              <span className="timeline-label">IVA Dual Pleno</span>
            </button>
          </div>

          {/* Linha 1: Situação Atual vs Reforma */}
          <div className="kpi-grid kpi-grid-4">
            <div className="kpi-card primary">
              <div className="card-content">
                <div className="thumb">
                  <FiDollarSign />
                </div>
                <div className="text">
                  <p>{formatNumber(reformaKpis.debitosAtuais)}</p>
                  <p>Débitos Atuais ({selectedCenario})</p>
                </div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="card-content">
                <div className="thumb">
                  <FiPercent />
                </div>
                <div className="text">
                  <p>{((reformaKpis.debitosAtuais / clienteData.faturamento) * 100).toFixed(1)}%</p>
                  <p>Alíquota Efetiva Atual</p>
                </div>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="card-content">
                <div className="thumb">
                  <GoGraph />
                </div>
                <div className="text">
                  <p>{formatNumber(reformaKpis.ivaDual)}</p>
                  <p>IVA Dual ({selectedCenario})</p>
                </div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="card-content">
                <div className="thumb">
                  <FiPercent />
                </div>
                <div className="text">
                  <p>{((reformaKpis.ivaDual / clienteData.faturamento) * 100).toFixed(1)}%</p>
                  <p>Alíquota Efetiva Projetada</p>
                </div>
              </div>
            </div>
          </div>

          {/* Linha 2: Detalhamento CBS e IBS */}
          <div className="kpi-grid kpi-grid-4">
            <div className="kpi-card tax-cbs">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(reformaKpis.cbs)}</p>
                  <p>CBS - Contribuição sobre Bens e Serviços</p>
                </div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="card-content">
                <div className="thumb">
                  <FiPercent />
                </div>
                <div className="text">
                  <p>{reformaKpis.aliquotaCBS.toFixed(2)}%</p>
                  <p>Alíquota CBS ({selectedCenario})</p>
                </div>
              </div>
            </div>
            <div className="kpi-card tax-ibs">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatNumber(reformaKpis.ibs)}</p>
                  <p>IBS - Imposto sobre Bens e Serviços</p>
                </div>
              </div>
            </div>
            <div className="kpi-card">
              <div className="card-content">
                <div className="thumb">
                  <FiPercent />
                </div>
                <div className="text">
                  <p>{reformaKpis.aliquotaIBS.toFixed(2)}%</p>
                  <p>Alíquota IBS ({selectedCenario})</p>
                </div>
              </div>
            </div>
          </div>

          {/* Linha 3: Análise de Impacto */}
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="kpi-card">
              <div className="card-content">
                <div className="thumb">
                  <FiDollarSign />
                </div>
                <div className="text">
                  <p>
                    {formatNumber(reformaKpis.impacto)}
                  </p>
                  <p>Diferença em Valores (R$)</p>
                </div>
              </div>
            </div>
            <div className={`kpi-card ${reformaKpis.impacto >= 0 ? 'warning' : 'success'}`}>
              <div className="card-content">
                <div className="thumb">
                  <FiTrendingUp />
                </div>
                <div className="text">
                  <p>
                    {reformaKpis.impacto >= 0 ? '+' : ''}{reformaKpis.variacaoPercentual.toFixed(1)}%
                  </p>
                  <p>Variação da Carga Tributária</p>
                </div>
              </div>
            </div>
          </div>

          {/* Linha 4: Comparativo Impostos Atuais vs Reforma - Donut Charts */}
          <div className="section-row">
            {/* Donut Chart - Sistema Atual */}
            <div className="stats-card chart-card">
              <div className="card-header">
                <div className="header-title">
                  <GoGraph />
                  <h4>Sistema Atual - Distribuição de Impostos ({selectedCenario})</h4>
                </div>
              </div>
              <div className="donut-wrapper" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", padding: "30px 24px", gap: "32px", minHeight: "280px" }}>
                <div className="donut-chart" style={{ flexShrink: 0 }}>
                  {(() => {
                    const taxes = [
                      { name: "ICMS", value: reformaKpis.icms, color: "#f59e0b" },
                      { name: "PIS", value: reformaKpis.pis, color: "#ef4444" },
                      { name: "COFINS", value: reformaKpis.cofins, color: "#10b981" },
                      { name: "IPI", value: reformaKpis.ipi, color: "#8b5cf6" },
                    ];
                    const activeTaxes = taxes.filter((t) => t.value > 0);
                    const total = taxes.reduce((sum, t) => sum + t.value, 0);

                    let currentAngle = -90;
                    const radius = 80;
                    const innerRadius = 50;
                    const cx = 100, cy = 100;

                    return (
                      <>
                        <svg width="180" height="180" viewBox="0 0 200 200">
                          {activeTaxes.map((tax, index) => {
                            const percentage = tax.value / total;
                            const angle = percentage * 360;
                            const startAngle = currentAngle;
                            const startRad = (startAngle * Math.PI) / 180;
                            const endRad = ((startAngle + angle) * Math.PI) / 180;
                            const largeArc = angle > 180 ? 1 : 0;

                            const x1 = cx + radius * Math.cos(startRad);
                            const y1 = cy + radius * Math.sin(startRad);
                            const x2 = cx + radius * Math.cos(endRad);
                            const y2 = cy + radius * Math.sin(endRad);
                            const ix1 = cx + innerRadius * Math.cos(startRad);
                            const iy1 = cy + innerRadius * Math.sin(startRad);
                            const ix2 = cx + innerRadius * Math.cos(endRad);
                            const iy2 = cy + innerRadius * Math.sin(endRad);

                            const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                            const midAngle = startAngle + angle / 2;
                            const midRad = (midAngle * Math.PI) / 180;
                            const labelR = (radius + innerRadius) / 2;
                            const labelX = cx + labelR * Math.cos(midRad);
                            const labelY = cy + labelR * Math.sin(midRad);

                            currentAngle += angle;

                            return (
                              <g key={index}>
                                <path d={path} fill={tax.color} />
                                {percentage > 0.08 && (
                                  <text
                                    x={labelX}
                                    y={labelY}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill="#fff"
                                    fontSize="11"
                                    fontWeight="600"
                                  >
                                    {(percentage * 100).toFixed(0)}%
                                  </text>
                                )}
                              </g>
                            );
                          })}
                        </svg>
                        <div className="donut-center">
                          <span className="donut-total">{formatNumber(total)}</span>
                          <span className="donut-label">Total</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="categories-list" style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                  {[
                    { name: "ICMS", value: reformaKpis.icms, color: "#f59e0b" },
                    { name: "PIS", value: reformaKpis.pis, color: "#ef4444" },
                    { name: "COFINS", value: reformaKpis.cofins, color: "#10b981" },
                    { name: "IPI", value: reformaKpis.ipi, color: "#8b5cf6" },
                  ].map((tax, index) => (
                    <div key={index} className="category-item" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                      <div className="category-header" style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                        <div
                          className="category-color"
                          style={{ backgroundColor: tax.color, width: "12px", height: "12px", borderRadius: "3px", flexShrink: 0 }}
                        ></div>
                        <span className="category-name" style={{ flex: 1, fontSize: "0.85em", fontWeight: 500 }}>{tax.name}</span>
                        <span className="category-count" style={{ fontSize: "0.8em", fontWeight: 600, padding: "4px 10px", borderRadius: "6px", background: "rgba(255,255,255,0.05)" }}>{formatNumber(tax.value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Donut Chart - IVA Dual */}
            <div className="stats-card chart-card">
              <div className="card-header">
                <div className="header-title">
                  <GoLaw />
                  <h4>Reforma Tributária - IVA Dual ({selectedCenario})</h4>
                </div>
              </div>
              <div className="donut-wrapper" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", padding: "30px 24px", gap: "32px", minHeight: "280px" }}>
                <div className="donut-chart" style={{ flexShrink: 0 }}>
                  {(() => {
                    const taxes = [
                      { name: "CBS", value: reformaKpis.cbs, color: "#f59e0b" },
                      { name: "IBS", value: reformaKpis.ibs, color: "#3b82f6" },
                    ];
                    const activeTaxes = taxes.filter((t) => t.value > 0);
                    const total = taxes.reduce((sum, t) => sum + t.value, 0);

                    let currentAngle = -90;
                    const radius = 80;
                    const innerRadius = 50;
                    const cx = 100, cy = 100;

                    return (
                      <>
                        <svg width="180" height="180" viewBox="0 0 200 200">
                          {activeTaxes.map((tax, index) => {
                            const percentage = tax.value / total;
                            const angle = percentage * 360;
                            const startAngle = currentAngle;
                            const startRad = (startAngle * Math.PI) / 180;
                            const endRad = ((startAngle + angle) * Math.PI) / 180;
                            const largeArc = angle > 180 ? 1 : 0;

                            const x1 = cx + radius * Math.cos(startRad);
                            const y1 = cy + radius * Math.sin(startRad);
                            const x2 = cx + radius * Math.cos(endRad);
                            const y2 = cy + radius * Math.sin(endRad);
                            const ix1 = cx + innerRadius * Math.cos(startRad);
                            const iy1 = cy + innerRadius * Math.sin(startRad);
                            const ix2 = cx + innerRadius * Math.cos(endRad);
                            const iy2 = cy + innerRadius * Math.sin(endRad);

                            const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                            const midAngle = startAngle + angle / 2;
                            const midRad = (midAngle * Math.PI) / 180;
                            const labelR = (radius + innerRadius) / 2;
                            const labelX = cx + labelR * Math.cos(midRad);
                            const labelY = cy + labelR * Math.sin(midRad);

                            currentAngle += angle;

                            return (
                              <g key={index}>
                                <path d={path} fill={tax.color} />
                                {percentage > 0.08 && (
                                  <text
                                    x={labelX}
                                    y={labelY}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill="#fff"
                                    fontSize="11"
                                    fontWeight="600"
                                  >
                                    {(percentage * 100).toFixed(0)}%
                                  </text>
                                )}
                              </g>
                            );
                          })}
                        </svg>
                        <div className="donut-center">
                          <span className="donut-total">{formatNumber(total)}</span>
                          <span className="donut-label">Total</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="categories-list" style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                  {[
                    { name: "CBS", value: reformaKpis.cbs, color: "#f59e0b" },
                    { name: "IBS", value: reformaKpis.ibs, color: "#3b82f6" },
                  ].map((tax, index) => (
                    <div key={index} className="category-item" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                      <div className="category-header" style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                        <div
                          className="category-color"
                          style={{ backgroundColor: tax.color, width: "12px", height: "12px", borderRadius: "3px", flexShrink: 0 }}
                        ></div>
                        <span className="category-name" style={{ flex: 1, fontSize: "0.85em", fontWeight: 500 }}>{tax.name}</span>
                        <span className="category-count" style={{ fontSize: "0.8em", fontWeight: 600, padding: "4px 10px", borderRadius: "6px", background: "rgba(255,255,255,0.05)" }}>{formatNumber(tax.value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </>
      )}
      </div>
    </Container>
  );
}
